import { useMemo, useSyncExternalStore } from 'react';
import { AiAvatar } from '../../../components/AiAvatar';
import type { StreamingMessage } from '../../../store/chatStore';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';
import { renderStreamingMarkdown } from '../../../lib/chat-markdown';

interface AiStreamingMessageProps {
  streaming: StreamingMessage;
  showAvatar: boolean;
}

/**
 * AI 응답 streaming 임시 메시지 (adr-20260430-2235-ai-gateway-streaming).
 *
 * delta chunk 가 도착하는 중에는 누적 text + cursor blink 표시. ai_complete 도달 시 cursor 숨김
 * (isStreaming=false). 정식 ChatMessageEvent 가 outbox 의 sse.message 로 도달하면 chatStore 가
 * 임시 entry 를 제거하고 MessageItem 으로 교체.
 *
 * 시안 정합 — chat-message-bubble--ai 와 같은 스타일 (Bot avatar gradient, primary-40 border).
 * cursor 는 CSS class `chat-message-streaming-cursor` 가 blink animation 책임.
 */
export function AiStreamingMessage({ streaming, showAvatar }: AiStreamingMessageProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  // 도착한 만큼을 markdown 으로 그린다 — 예전엔 날것 텍스트라 ```html 표의 태그가 그대로 보였다.
  // chunk 마다 재파싱하므로 text/isStreaming 이 그대로면 다시 만들지 않는다.
  const body = useMemo(
    () => renderStreamingMarkdown(streaming.text, streaming.isStreaming),
    [streaming.text, streaming.isStreaming],
  );
  return (
    <div
      className={`chat-message-row${showAvatar ? ' chat-message-row--show-avatar' : ' chat-message-row--continuation'} chat-message-row--streaming`}
      data-stream-message-id={streaming.streamMessageId}
      aria-busy={streaming.isStreaming}
      aria-live="polite"
    >
      <div className="chat-message-avatar-slot">
        {showAvatar && (
          <div className="chat-message-avatar chat-message-avatar--ai" aria-label={tChat('chat.ai_stream.aria_avatar_responding')}>
            <AiAvatar />
          </div>
        )}
      </div>
      <div className="chat-message-body">
        {showAvatar && (
          <div className="chat-message-meta">
            <span className="chat-message-sender">{tChat('chat.item.ai_assistant')}</span>
            <span className="chat-message-ai-badge">{tChat('chat.item.ai_badge')}</span>
            {streaming.isStreaming && (
              <span className="chat-message-streaming-status" aria-label={tChat('chat.ai_stream.aria_status')}>{tChat('chat.ai_stream.status_text')}</span>
            )}
          </div>
        )}
        <div className="chat-message-bubble chat-message-bubble--ai chat-message-bubble--streaming">
          {body}
          {streaming.isStreaming && (
            <span className="chat-message-streaming-cursor" aria-hidden="true">▍</span>
          )}
        </div>
      </div>
    </div>
  );
}
